import React from 'react'
import { styled } from '@mui/system'
import { Container, Typography, Grid, Button, Tooltip } from '@mui/material'
import { generateUtilityClasses } from '@mui/material'
import { ReactComponent as PrintIcon } from '../../../../../assets/icons/printer.svg'
import { ReactComponent as DownloadIcon } from '../../../../../assets/icons/download_icon.svg'
import { ReactComponent as FilterIcon } from '../../../../../assets/icons/filter_icon.svg'
import { UspReportAccountsChargeOffsResult } from '../../../../../models'
import * as XLSX from 'xlsx'
import { parseToNumber } from '../../../../../utils/general'
import { convertDateToDisplay } from '../../../../../utils/dateUtils'

const classes = generateUtilityClasses('Container', ['root', 'titleWrap', 'btnWrap'])

const StyledContainer = styled(Container)(({ theme }) => ({
  [`&.${classes.root}`]: {
    padding: theme.spacing(1, 3, 1, 3),
    margin: 0,
    display: 'flex',
    backgroundColor: theme.palette.background.main,
    width: '100%',
    maxWidth: 'unset',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  [`& .${classes.titleWrap}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  [`& .${classes.btnWrap}`]: {
    maxWidth: '30px',
    maxHeight: '42px',
    minWidth: '30px',
    minHeight: '42px',
    padding: '3px',
    marginLeft: '10px',
    color: theme.palette.text.primary,
  },
}))

interface Props {
  handleOpenFilterModal: () => void
  filterData: UspReportAccountsChargeOffsResult[]
}

export const Header = ({ handleOpenFilterModal, filterData }: Props) => {
  const transformDataForExport = (data: UspReportAccountsChargeOffsResult[]) => {
    return data?.map((item: UspReportAccountsChargeOffsResult) => ({
      'Stock #': item.StockNumber,
      Customer: item.Customer,
      'Transaction Type': item?.TransactionType as string,
      'Effective Date': convertDateToDisplay(item?.EffectiveDate as string),
      'Account #': item.AccountNumber,
      Branch: item.Branch,
      Dealer: item.Dealer,
      Collector: item.Collector,
      Principal: parseToNumber(item.BalancePrincipal),
      Gross: parseToNumber(item.BalanceGross),
      Pickups: parseToNumber(item.BalancePickups),
      Chargeback: parseToNumber(item.BalanceChargeback),
      'Late Charge': parseToNumber(item.BalanceLateCharge),
      'Side Notes': parseToNumber(item.BalanceSideNotes),
      'Late Charge Due': parseToNumber(item?.LateChargeDue as string),
      'Repo ACV': parseToNumber(item?.RepoACV as string),
      'Total Losses': parseToNumber(item?.TotalLosses as string),
      'Unearned Discount': parseToNumber(item?.UnearnedDiscount as string),
      'Interest Due': parseToNumber(item?.InterestDue as string),
      'Internal Loss': parseToNumber(item?.InternalLoss as string),
      Deficiency: parseToNumber(item.BalanceDeficiency),
    }))
  }

  const setColumnWidths = (worksheet: any) => {
    const columnWidths = [
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
    ]

    worksheet['!cols'] = columnWidths
  }

  const exportToExcel = (data: any, fileName: any) => {
    const transformedData = transformDataForExport(data)
    const worksheet = XLSX.utils.json_to_sheet(transformedData)
    setColumnWidths(worksheet)

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
    XLSX.writeFile(workbook, `${fileName}.xlsx`)
  }

  return (
    <>
      <Grid container justifyContent={'flex-end'} p={3} alignItems={'center'}>
        <Grid item>
          <Tooltip title='Download'>
            <Button variant='text' className={classes.btnWrap} onClick={() => exportToExcel(filterData, 'Charge Offs')}>
              <DownloadIcon />
            </Button>
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip title='Print'>
            <Button variant='text' className={classes.btnWrap}>
              <PrintIcon />
            </Button>
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip title='Filter'>
            <Button onClick={handleOpenFilterModal} variant='text' className={classes.btnWrap}>
              <FilterIcon />
            </Button>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  )
}

export default Header
